import { TextField } from '@quintype/em/components/text-field';
import { Checkbox } from '@quintype/em/components/checkbox';
import React from 'react';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import { updateHaveLayoutUpdates, updateHaveUnsavedChanges, updatePageSettings } from '../../actions/builder-action';
import { updateRowsList } from '../../actions/row-action';
import { ToggleButton } from '../toggle-button';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styles from './subscription.module.css';
import { FieldLabel } from '@quintype/em/components/field-label';
import { ThemeSetter } from '../theme-setter';
import { Separator } from '../../atoms/separator';
import { updateMeterSettings } from '../../actions/builder-action';
import {
  SUBSCRIPTION_LAYOUTS,
  LOGINLESS_LAYOUTS,
  getLoginlessSettings
} from './constants';

interface IDataItems {
  type: string;
  label: string;
  key: string;
  defaultValue?: string | boolean;
  placeHolder?: string;
  darkKey?: string;
}

export const SubscriptionSettings = () => {
  const dispatch = useDispatch();

  const { subscription = {}, enableDarkModePreview = false } = useSelector(state =>
    get(state, ['builder', 'config'], {})
  );
  const { pageSetting = {}, rows = [] } = subscription;
  const updateSubscription = (key: string, value: string | boolean) => {
    if (key === 'mandatoryEmail' && !value) {
      const isPhoneChecked = Boolean(get(pageSetting, ['mandatoryPhone'], false));
      if (!isPhoneChecked) {
        // Email must be checked if phone number is not checked (neither can be unchecked)
        return;
      }
    } else if (key === 'mandatoryPhone' && !value) {
      const isEmailChecked = Boolean(get(pageSetting, ['mandatoryEmail'], true));
      if (!isEmailChecked) {
        // If unchecking phone leaves email unchecked, email must be checked by default
        dispatch(updatePageSettings({ mandatoryPhone: false, mandatoryEmail: true }));
        dispatch(updateHaveUnsavedChanges(true));
        return;
      }
    }

    dispatch(updatePageSettings({ [key]: value }));

    if (key === 'enableLoginlessSubscription') {
      const subRow = rows.find((r: any) => SUBSCRIPTION_LAYOUTS.includes(r.layout));
      const currentRow = subRow || rows[0];
      const currentLayout = currentRow?.layout;

      if (value && currentLayout && !LOGINLESS_LAYOUTS.includes(currentLayout)) {
        dispatch(updatePageSettings({ selectedLayout: currentLayout }));
      }

      const selectedLayout = get(pageSetting, ['selectedLayout'], 'Lavish');
      const targetLayout = value ? 'LoginlessFlat' : selectedLayout;
      const currentTitle = get(pageSetting, ['pageTitle']);
      const defaultPageTitle = value ? 'Upgrade to Premium' : 'Discover the Power of News Subscription Plans';
      const shouldUpdateTitle =
        currentTitle === undefined ||
        currentTitle === 'Discover the Power of News Subscription Plans' ||
        currentTitle === 'Upgrade to Premium';

      if (shouldUpdateTitle) {
        dispatch(updatePageSettings({ pageTitle: defaultPageTitle }));
      }

      if (currentRow?.rowId) {
        dispatch(updateRowsList({ rowAction: 'update', rowActionPayload: { selectedRowId: currentRow.rowId, row: { ...currentRow, layout: targetLayout, layoutLabel: targetLayout } } }));
      }

      if (!value) {
        rows.forEach((row: any) => {
          if (
            row?.rowId &&
            (row.type === 'ads' ||
              row.type === 'widget' ||
              !SUBSCRIPTION_LAYOUTS.includes(row.layout))
          ) {
            dispatch(updateRowsList({ rowAction: 'delete_row', rowActionPayload: { selectedRowId: row.rowId, row: {} } }));
          }
        });
      }

      dispatch(updateHaveLayoutUpdates(true));
    }

    switch (key) {
      case 'buttonColor':
        dispatch(updateMeterSettings({ key: 'subscribeButtonColor', value }));
        break;
      case 'darkButtonColor':
        dispatch(updateMeterSettings({ key: 'subscribeDarkButtonColor', value }));
        break;
    }

    dispatch(updateHaveUnsavedChanges(true));
  };

  const getSubscriptionSetting = (layout: string) => {
    const displayGroupImage = {
      label: 'Display Group Images',
      key: 'showGroupImg',
      type: 'toggle'
    };

    const displayPlanImage = {
      label: 'Display Plan Images',
      key: 'showPlanImg',
      type: 'toggle'
    };

    const loaderSetting = [
      {
        label: 'Separator',
        key: 'separator',
        type: 'separator'
      },
      {
        label: 'Loading Delay Message',
        type: 'quillEditor',
        defaultValue: 'Oops, it seems plans are taking longer than expected to load. Thank you for your patience!',
        key: 'loaderMessage',
        placeHolder: 'Oops, it seems plans are taking longer than expected to load. Thank you for your patience!'
      }
    ];

    const setting = [
      {
        label: 'Page Title',
        type: 'quillEditor',
        defaultValue: 'Discover the Power of News Subscription Plans',
        key: 'pageTitle',
        placeHolder: 'Discover the Power of News Subscription Plans'
      },
      {
        label: 'Page Description',
        type: 'quillEditor',
        defaultValue:
          'Unlock premium news content and stay informed with our exclusive subscription plan. Get access to in-depth stories and expert analysis.',
        key: 'pageDescription',
        placeHolder:
          'Unlock premium news content and stay informed with our exclusive subscription plan. Get access to in-depth stories and expert analysis.'
      },
      {
        label: 'Subscribe Button Text',
        placeHolder: 'Subscribe Now',
        key: 'subscribeButton',
        defaultValue: 'Subscribe Now',
        type: 'textField'
      },
      {
        label: "What's Included Text",
        placeHolder: "What's Included",
        key: 'descriptionTitle',
        defaultValue: "What's Included",
        type: 'textField'
      },
      {
        type: 'colorPicker',
        label: 'Button Color',
        key: 'buttonColor',
        darkKey: 'darkButtonColor'
      },
      {
        label: 'Display Original Price & Offer',
        key: 'showOfferPrice',
        type: 'toggle'
      }
    ];

    const thankYouPageSettings = [
      {
        label: 'Separator',
        key: 'separator2',
        type: 'separator'
      },
      {
        label: 'Thank you page',
        type: 'fieldLabel',
        key: 'fieldLabel2'
      },
      {
        label: 'Title',
        type: 'quillEditor',
        defaultValue: 'Thank you for placing your order!',
        key: 'thankYouPageTitle',
        placeHolder: ''
      },
      {
        label: 'Description',
        type: 'quillEditor',
        defaultValue:
          'We kindly ask for your patience as we process it (approx. 15min). Please check your email for further details and updates.',
        key: 'thankYouPageDescription',
        placeHolder: ''
      },
      {
        label: 'Continue Reading button',
        placeHolder: 'Continue Reading',
        key: 'continueReading',
        defaultValue: 'Continue Reading',
        type: 'textField'
      },
      {
        label: 'Go to Profile button',
        placeHolder: '',
        key: 'goToProfile',
        defaultValue: 'Go to Profile',
        type: 'textField'
      },
      {
        label: 'Go to Home button',
        placeHolder: '',
        key: 'goToHome',
        defaultValue: 'Go to Home',
        type: 'textField'
      }
    ];

    switch (layout) {
      case 'Lavish':
        return [...setting, displayGroupImage, ...loaderSetting, ...thankYouPageSettings];
      case 'Concrete':
        return [...setting, displayPlanImage, ...loaderSetting, ...thankYouPageSettings];
      default:
        return [...setting, ...loaderSetting, ...thankYouPageSettings];
    }
  };

  const isLoginlessEnabled = get(pageSetting, ['enableLoginlessSubscription'], false);
  const subRow = rows.find((r: any) => SUBSCRIPTION_LAYOUTS.includes(r.layout));
  const layout = get(subRow || rows[0], ['layout'], 'Lavish');


  // ─── Shared renderer ─────────────────────────────────────────────────────────

  const renderSetting = (item: IDataItems) => {
    const { type, label, key, defaultValue = '', placeHolder, darkKey = '' } = item || {};

    const currentValue = get(pageSetting, [key], '');
    const placeholderText = placeHolder || (defaultValue as string) || '';

    switch (type) {
      case 'quillEditor': {
        const modules = {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }]
          ]
        };

        const formats = ['bold', 'italic', 'underline', 'strike', 'list', 'bullet'];
        return (
          <div className={styles.quillWrapper} key={key}>
            <FieldLabel label={label} />
            <ReactQuill
              theme="snow"
              modules={modules}
              formats={formats}
              value={currentValue as string}
              onChange={(value: any) => {
                const cleanedValue = value === '<p><br></p>' ? '' : value;
                updateSubscription(key, cleanedValue);
              }}
              placeholder={placeholderText}
            />
          </div>
        );
      }
      case 'textField':
        return (
          <TextField
            key={key}
            label={label}
            placeholder={placeholderText}
            value={currentValue as string}
            onChange={(value: any) => updateSubscription(key, value)}
          />
        );
      case 'toggle':
        return (
          <div className={styles.toggleContainer} key={key}>
            <ToggleButton
              label={label}
              toggleName={key}
              id={key}
              updateToggleState={get(pageSetting, [key], defaultValue)}
              updateToggleButton={(name: string, value: boolean) => updateSubscription(name, value)}
            />
          </div>
        );
      case 'colorPicker':
        return (
          <div className={styles.colorPickerContainer} key={key}>
            <ThemeSetter
              label={label}
              updateTheStore={updateSubscription}
              lightColorExtract={get(pageSetting, [key], defaultValue as string)}
              lightUpdateStoreKey={key}
              darkColorExtract={get(pageSetting, [darkKey], defaultValue as string)}
              darkUpdateStoreKey={darkKey}
              lightDefaultColor="#4860BC"
              darkDefaultColor="#4860BC"
              enableDarkModePreview={enableDarkModePreview}
            />
          </div>
        );
      case 'separator':
        return <Separator key={key} />;
      case 'fieldLabel':
        return (
          <div className={styles.fieldLabel} key={key}>
            <FieldLabel label={label} />
          </div>
        );
      case 'checkbox': {
        const isPhoneChecked = Boolean(get(pageSetting, ['mandatoryPhone'], false));
        let isChecked: boolean;
        if (key === 'mandatoryEmail') {
          isChecked = !isPhoneChecked ? true : Boolean(get(pageSetting, [key], defaultValue));
        } else {
          isChecked = Boolean(get(pageSetting, [key], defaultValue));
        }

        return (
          <div className={styles.checkboxWrapper} key={key}>
            <Checkbox
              label={label}
              id={key}
              checked={isChecked}
              disabled={false}
              onChange={(value: boolean) => updateSubscription(key, value)}
            />
          </div>
        );
      }
      case 'hintText':
        return (
          <p className={styles.hintText} key={key}>
            {label}
          </p>
        );
      default:
        return null;
    }
  };

  const settingsToRender = isLoginlessEnabled ? getLoginlessSettings() : getSubscriptionSetting(layout);

  return (
    <>
      {/* Enable Loginless Subscription — always visible at the top */}
      <div className={styles.toggleContainer}>
        <ToggleButton
          label="Enable Loginless Subscription"
          toggleName="enableLoginlessSubscription"
          id="enableLoginlessSubscription"
          updateToggleState={isLoginlessEnabled}
          updateToggleButton={(name: string, value: boolean) => updateSubscription(name, value)}
        />
      </div>
      {isLoginlessEnabled && (
        <div className={styles.hintText}>
          Loginless Subscription comes with its own dedicated templates. Other templates can&apos;t be used while this
          is enabled.
        </div>
      )}

      {settingsToRender.map((item: IDataItems) => renderSetting(item))}
    </>
  );
};
